import {
  LayersIcon,
  TrendingUpIcon,
  TargetIcon,
  FlameIcon,
  BatteryChargingIcon,
  FlagIcon,
  CalendarDaysIcon,
  ZapIcon,
  ClipboardCheckIcon,
  ActivityIcon,
} from 'lucide-react';

export const phases = [
  {
    n: '01',
    name: 'Foundation',
    short: 'Build the base.',
    weeks: 'Weeks 1–8',
    icon: LayersIcon,
    title: 'The Foundation',
    lead: 'Build the foundation your full-distance preparation depends on.',
    desc: 'Establish the structure, consistency and training habits required to begin building toward the demands of a full-distance event.',
  },
  {
    n: '02',
    name: 'Build',
    short: 'Develop your endurance.',
    weeks: 'Weeks 9–20',
    icon: TrendingUpIcon,
    title: 'The Build',
    lead: 'Develop your endurance through progressive training.',
    desc: 'Organize your swim, bike and run development as your training volume and demands begin to increase.',
  },
  {
    n: '03',
    name: 'Specific',
    short: 'Train for the demands of the distance.',
    weeks: 'Weeks 21–30',
    icon: TargetIcon,
    title: 'The Specific Phase',
    lead: 'Start training for the demands of the actual distance.',
    desc: 'Move toward more race-specific preparation and understand how your training should evolve as you get closer to the demands of the event.',
  },
  {
    n: '04',
    name: 'Peak',
    short: 'Bring your preparation together.',
    weeks: 'Weeks 31–35',
    icon: FlameIcon,
    title: 'The Peak',
    lead: 'Bring your preparation together.',
    desc: 'The peak phase connects the different elements of your training so your preparation becomes increasingly specific to your race objective.',
  },
  {
    n: '05',
    name: 'Taper',
    short: 'Arrive ready — not exhausted.',
    weeks: 'Weeks 36–40',
    icon: BatteryChargingIcon,
    title: 'The Taper',
    lead: 'Reduce the load while maintaining readiness.',
    desc: 'Understand the purpose of the final phase before race day and organize your preparation so you can arrive at the starting line ready to execute.',
  },
  {
    n: '06',
    name: 'Race Execution',
    short: 'Turn your preparation into race-day execution.',
    weeks: 'Race Day',
    icon: FlagIcon,
    title: 'The Race Execution Framework',
    lead: 'Training gets you to the starting line. Execution gets you through the race.',
    desc: 'Bring together pacing, fueling, transitions, preparation and race-day decision making into one practical execution framework.',
  },
];

export const tools = [
  { verb: 'Plan', name: '40-Week Triathlete Training Calendar™', icon: CalendarDaysIcon },
  { verb: 'Fuel', name: 'The Triathlete Fueling Vault™', icon: ZapIcon },
  { verb: 'Prepare', name: 'The Race Week Command Center™', icon: ClipboardCheckIcon },
  { verb: 'Track', name: 'The Triathlete Progress Tracker™', icon: ActivityIcon },
];

export const systemIncludes = [
  'Swim training frameworks',
  'Bike training structures',
  'Run progression concepts',
  'Strength & supporting work',
  'Recovery considerations',
  'Training organization',
  'Progress tracking',
  'Fueling principles',
  'Hydration considerations',
  'Race preparation frameworks',
  'Race execution concepts',
  'Practical checklists',
];

export type BonusAccent = 'electric' | 'perf' | 'ember' | 'aqua';

export const bonuses: {
  n: string;
  accent: BonusAccent;
  title: string;
  subtitle: string;
  desc: string[];
  tags: string[];
  price: string;
  cover: string;
}[] = [
  {
    n: '01',
    accent: 'electric',
    title: 'The 40-Week Triathlete Training Calendar™',
    subtitle: 'Your visual roadmap from starting line to race day',
    desc: [
      'Stop wondering what your long-term preparation should look like.',
      'The 40-Week Triathlete Training Calendar™ gives you a visual roadmap through the major stages of your preparation.',
    ],
    tags: ['Weeks 1–8 · Foundation', 'Weeks 9–20 · Build', 'Weeks 21–30 · Specific', 'Weeks 31–35 · Peak', 'Weeks 36–40 · Taper'],
    price: '$32',
    cover: 'https://cdn.magicpatterns.com/uploads/neH3Jb5qBk5aDF1nzuVFCn/Calendario_de_entrenamiento_triatlon_de_40_semanas.webp',
  },
  {
    n: '02',
    accent: 'perf',
    title: 'The Triathlete Fueling Vault™',
    subtitle: 'Your training & race-day nutrition toolkit',
    desc: [
      'Training demands energy. Long-distance racing demands a strategy.',
      'The Triathlete Fueling Vault™ helps you organize the nutrition and hydration side of your preparation.',
    ],
    tags: ['Everyday nutrition', 'Pre-workout fueling', 'Hydration & electrolytes', 'During-training fueling', 'Recovery nutrition', 'Race-day fueling'],
    price: '$29',
    cover: 'https://cdn.magicpatterns.com/uploads/m1L8bmWxb4JwvhTnCuX1Ab/Calendario_de_entrenamiento_triatlon_de_40_semanas_(2).webp',
  },
  {
    n: '03',
    accent: 'ember',
    title: 'The Race Week Command Center™',
    subtitle: 'Your 7-day checklist from taper to starting line',
    desc: [
      "The final week shouldn't be a collection of last-minute decisions.",
      'The Race Week Command Center™ helps you organize the final days leading into your event.',
    ],
    tags: ['Planning', 'Travel', 'Equipment', 'Swim prep', 'Bike prep', 'Run prep', 'Race morning', 'Starting line'],
    price: '$32',
    cover: 'https://cdn.magicpatterns.com/uploads/2ggenKBbn3CGPj8n7GeBAp/Calendario_de_entrenamiento_triatlon_de_40_semanas_(1).webp',
  },
  {
    n: '04',
    accent: 'aqua',
    title: 'The Triathlete Progress Tracker™',
    subtitle: 'Track your training. Understand your progress. Stay consistent.',
    desc: [
      'Training becomes more useful when you can see what is actually happening.',
      'Use the Triathlete Progress Tracker™ to organize and monitor your preparation across the three disciplines.',
    ],
    tags: ['Swim', 'Bike', 'Run', 'Training load', 'Recovery', 'Weekly consistency'],
    price: '$27',
    cover: 'https://cdn.magicpatterns.com/uploads/wHiFt6bamHbHibKMcnkgJm/Calendario_de_entrenamiento_triatlon_de_40_semanas_(3).webp',
  },
];

export const idealFor = [
  'Triathletes preparing for a full-distance event.',
  'Endurance athletes looking for a structured framework.',
  'Athletes who want to organize swim, bike and run training.',
  'Beginners who need a clear roadmap.',
  'Experienced athletes who want a more structured preparation process.',
];

export const offerIncludes = [
  '6-Step Full-Distance Blueprint',
  'Foundation Framework',
  'Build Framework',
  'Specific Phase Framework',
  'Peak Framework',
  'Taper Framework',
  'Race Execution Framework',
  'Swim / Bike / Run Training Structure',
  'Fueling & Hydration Framework',
  'Race Preparation Framework',
];
